import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import {
  applySlip,
  CalAm,
  getInitFormData,
  setConfigToColumns,
} from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global, useMenuStore } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import SnResizeBar from "@shared/components/SnResizeBar";
import { dateToString } from "@shared/utils/DateUtils";
import {
  CellValueChangedEvent,
  SnGridRef,
} from "@shared/components/GridV2/SnGridTypes";
import { RowNode } from "ag-grid-community";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
interface Props {
  Menuprops: menuInfo;
}

const SN_PUR_PRQ_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo, config } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [selectedDocu, setSelectedDocu] = useState<string>(""); // file 관련

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData(rtn.formData);

        const configGrid1 = setConfigToColumns(rtn.grid.grid2, "MAS0002");
        const configGrid2 = setConfigToColumns(configGrid1, "PUR0105");
        setColums({
          grid1: rtn.grid.grid1,
          grid2: configGrid2,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  const onSearch = async () => {
    const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
    const modifiedRowsL = gridRefs.current["grid2"].current.getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved = await onSave();
        if (!isSaved) return;
      }
    }

    try {
      const params = {
        id: "sn_pur_prq_reg_001_01",
        params: searchRef.current.getFormdata(),
      };

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
      });
    } catch (error: any) {}
  };

  const onInsert = async () => {
    try {
      const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
      if (modifiedRowsH.modifiedRowcount > 0) {
        await showDialog("alter", "수정된 내용이 있습니다.");
        return;
      }

      const today = dateToString(new Date());
      const newRow = {
        cd_firm: userInfo.cd_firm,
        cd_biz: userInfo.cd_biz,
        dt_prq: today,
        cd_dept: userInfo.cd_dept,
        nm_gp: userInfo.nm_gp,
        cd_gp: userInfo.cd_gp,
        st_req: "P",
        cd_emp: userInfo.cd_emp,
        nm_emp: userInfo.nm_emp,
        fg_prq: "ETC",
      };
      gridRefs.current["grid1"].current?.addRow(newRow);
    } catch (error: any) {}
  };

  const onAddRow = () => {
    try {
      return gridRefs.current["grid2"].current?.addRow();
    } catch (error: any) {}
  };

  const onBeforeAddRow2 = (): any => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();
      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const focusedDataH = focusedNodeH.data;
      if (!focusedDataH) return;

      const st_req = focusedNodeH.data.st_req;
      if (st_req === "C") return;

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const maxNoLine =
        gridRefs.current["grid2"].current.getMaxValue("no_line");
      return {
        cd_firm: userInfo.cd_firm,
        cd_cust: "",
        dt_dlv: dateToString(new Date()),
        no_line: maxNoLine + 1,
        no_prq: focusedDataH.no_prq,
        qt: 0,
        qt_app: 0,
        qt_po: 0,
        no_line_so: 0,
        no_line_woreq: 0,
      };
    } catch (error: any) {
      return false;
    }
  };

  const onDelete = async () => {
    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() < 1) return;

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) return;

    gridRefs.current["grid2"].current?.deleteAllRows();
    gridRefs.current["grid1"].current?.deleteSelectedRows();
    const realDeletedRows =
      gridRefs.current["grid1"].current.getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      return;
    }

    return await onSave();
  };

  const onDeleteRow = async () => {
    if (gridRefs.current["grid2"].current?.getDisplayedRowCount() < 1) return;

    const selectedNodes = gridRefs.current["grid2"].current?.focusedRowNode();
    if (!selectedNodes) return;

    const { qt_po, qt_app } = selectedNodes.data ?? {};
    if ((qt_po && qt_po !== 0) || (qt_app && qt_app !== 0)) return;

    gridRefs.current["grid2"].current?.deleteSelectedRows();
    setFooterDataToRow();
  };

  const onSave = async () => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();
      gridRefs.current["grid2"].current?.stopEditing();

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const requiredValuesCheckL =
        gridRefs.current["grid2"].current.requiredValuesCheck();
      if (!requiredValuesCheckL) return false;

      const purPrqHChanges = gridRefs.current["grid1"].current.getChange();
      const purPrqLChanges = gridRefs.current["grid2"].current.getChange();
      const headerInserts = purPrqHChanges.insert ?? [];
      const lineInserts = purPrqLChanges.insert ?? [];
      if (headerInserts.length > 0 && lineInserts.length === 0) {
        await showDialog("alter", "저장에 실패했습니다.");
        return false;
      }

      if (
        purPrqHChanges.modifiedRowcount === 0 &&
        purPrqLChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const focusedRowNodeH =
        gridRefs.current["grid1"].current.focusedRowNode();
      const noSlip = await applySlip({
        cd_column: "no_prq",
        cd_module: Menuprops.cd_module,
        no_line: 1,
        cd_prefix: "",
        dt: focusedRowNodeH.data.dt_prq,
        girdrefs: [gridRefs.current["grid1"], gridRefs.current["grid2"]],
        inserts: [purPrqHChanges, purPrqLChanges],
        cd_menu: Menuprops.cd_menu,
      });
      gridRefs.current["grid2"].current.applyFilters({ no_prq: noSlip });

      const saveData = {
        pur_prqh: purPrqHChanges,
        pur_prql: purPrqLChanges,
      };
      const ok = await fnSave("SN_PUR_PRQ_REG_001_SAVE_01", saveData);
      if (!ok) return;

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      return true;
    } catch (error: any) {
      gridRefs.current["grid1"].current.resetUpdate(true);
      gridRefs.current["grid2"].current.resetUpdate(true);
      return false;
    }
  };

  const onGrid2BtnsClick = async (id: string) => {
    if (id == "addRow") {
      onAddRow();
    }
    if (id == "deleteRow") {
      onDeleteRow();
    }
  };

  const onFocusedRowChanged = (event: any) => {
    const selectedRow = event;
    if (selectedRow) {
      const no_prq = selectedRow.no_prq;
      gridRefs.current["grid2"].current.applyFilters({ no_prq: no_prq });
      gridRefs.current["grid2"].current.selectAndFocusRow(0);
      setSelectedDocu(no_prq);
    } else {
      return;
    }
  };

  const onCellValueChanged = async (event: CellValueChangedEvent) => {
    if (!event) return;
    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "cd_cust") {
      const rowCount = getGridCurrent("grid2").getDisplayedRowCount();
      if (rowCount === 1) return;
      if (!rowData.cd_cust) return;

      if (rowData.yn_condition === "Y") {
        rowData.yn_condition = "N";
        return;
      }

      let apply = false;
      getGridCurrent("grid2")?.forEachFilteredNode((node: RowNode) => {
        if (
          node.data[colId] === "" ||
          node.data[colId] === null ||
          node.data[colId] === undefined
        )
          apply = true;
      });
      if (!apply) return;

      const confirm = await showDialog(
        "confirm",
        "일괄적용하시겠습니까?(비어있는 칸이 채워집니다.)"
      );

      if (confirm) {
        getGridCurrent("grid2")?.forEachFilteredNode((node: RowNode) => {
          if (
            node.data.cd_cust === "" ||
            node.data.cd_cust === null ||
            node.data.cd_cust === undefined
          ) {
            node.setDataValue("cd_cust", rowData.cd_cust);
            node.setDataValue("nm_cust", rowData.nm_cust_sort);
            node.setDataValue("nm_cust_sort", rowData.nm_cust_sort);
          }
        });
      }
    }
    if (colId === "dt_dlv") {
      const rowCount = getGridCurrent("grid2").getDisplayedRowCount();
      if (rowCount === 1) return;

      let apply = false;
      getGridCurrent("grid2")?.forEachFilteredNode((node: RowNode) => {
        if (
          node.data[colId] === "" ||
          node.data[colId] === null ||
          node.data[colId] === undefined
        )
          apply = true;
      });
      if (!apply) return;

      const confirm = await showDialog("confirm", "일괄적용하시겠습니까?");
      if (confirm) {
        const today = dateToString(new Date());
        getGridCurrent("grid2")?.forEachFilteredNode((node: RowNode) => {
          if (
            node.data[colId] === "" ||
            node.data[colId] === null ||
            node.data[colId] === undefined
          ) {
            node.setDataValue("dt_dlv", rowData.dt_dlv);

            const dtDlv = node.data.dt_dlv;
            if (!dtDlv || dtDlv.substring(6, 8) === "00") return;
            if (Number(dtDlv) - 1 > Number(today)) {
              node.setDataValue("dt_isu_plan", Number(dtDlv) - 1);
            } else {
              node.setDataValue("dt_isu_plan", today);
            }
          }
        });
      }
    }

    if (colId === "cd_pjt") {
      const rowCount = getGridCurrent("grid2").getDisplayedRowCount();
      if (rowCount === 1) return;
      if (!rowData.cd_pjt) return;

      let apply = false;
      getGridCurrent("grid2")?.forEachFilteredNode((node: RowNode) => {
        if (
          node.data[colId] === "" ||
          node.data[colId] === null ||
          node.data[colId] === undefined
        )
          apply = true;
      });
      if (!apply) return;

      const confirm = await showDialog(
        "confirm",
        "일괄적용하시겠습니까?(비어있는 칸이 채워집니다.)"
      );

      if (confirm) {
        getGridCurrent("grid2")?.forEachFilteredNode((node: RowNode) => {
          if (
            node.data.cd_pjt === "" ||
            node.data.cd_pjt === null ||
            node.data.cd_pjt === undefined
          ) {
            node.setDataValue("cd_pjt", rowData.cd_pjt);
            node.setDataValue("nm_pjt", rowData.nm_pjt);
          }
        });
      }
    }

    CalAm(gridRefs.current["grid1"], gridRefs.current["grid2"], colId);
  };

  const setFooterDataToRow = () => {
    const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
    const footerData = gridRefs.current["grid2"].current.getFooterData();
    if (focusedNodeH) {
      focusedNodeH.setDataValue("am", footerData.am);
      focusedNodeH.setDataValue("am_k", footerData.am_k);
      focusedNodeH.setDataValue("am_vat", footerData.am_vat);
      focusedNodeH.setDataValue("am_supply", footerData.am_supply);
    }
  };

  const handleContextMenu = (params: any) => {
    const menuItems = useMenuStore.getState().menuItems;
    switch (params) {
      case "fn_sn_pur_com_app_reg_01": //구매품의등록
        const selectedNodeH =
          gridRefs.current["grid1"].current.focusedRowNode();
        const selectedStReq = selectedNodeH?.data.st_req;
        if (selectedStReq.toString() === "C") {
          showDialog("alter", "미승인 건이 있습니다.");
          return;
        }

        const selectedDataH =
          gridRefs.current["grid1"].current.getSelectedRows();
        const selectedRows =
          gridRefs.current["grid2"].current.getSelectedRows();
        let noLineString = "";
        selectedRows.forEach((row: any) => {
          noLineString += row.no_line + "|";
        });
        const returnData = [selectedDataH[0].no_prq, noLineString];
        const drillDown = menuItems
          ? menuItems.find(
              (item: any) => item?.cd_menu === "SN_PUR_APP_REG_001"
            )
          : undefined;
        if (drillDown) {
          const filePath =
            `./${drillDown.cd_module}/${drillDown.cd_menu}/${drillDown.cd_menu}`.toUpperCase();
          (window as any).onActivate(
            drillDown.cd_menu, // 메뉴 키
            drillDown.nm_menu, // 메뉴 제목
            drillDown.cd_module, // 모듈
            filePath, // 파일 경로
            drillDown, // 데이터
            drillDown.nm_path,
            returnData,
            "fg_drilldown.Registration"
          );
        }
        break;

      case "fn_sn_pur_com_app_reg_02": //발주등록
        const selectedRowH =
          gridRefs.current["grid1"].current.getSelectedRows();
        const selectedRowL =
          gridRefs.current["grid2"].current.getSelectedRows();
        let noLine = "";
        selectedRowL.forEach((row: any) => {
          noLine += row.no_line + "|";
        });
        const primaryData = [selectedRowH[0].no_prq, noLine];
        const targetMenu = menuItems
          ? menuItems.find(
              (item: any) => item?.cd_menu === "SN_PUR_POR_REG_001"
            )
          : undefined;
        if (targetMenu) {
          const filePathStr =
            `./${targetMenu.cd_module}/${targetMenu.cd_menu}/${targetMenu.cd_menu}`.toUpperCase();
          (window as any).onActivate(
            targetMenu.cd_menu, // 메뉴 키
            targetMenu.nm_menu, // 메뉴 제목
            targetMenu.cd_module, // 모듈
            filePathStr, // 파일 경로
            targetMenu, // 데이터
            targetMenu.nm_path,
            primaryData,
            "fg_drilldown.Registration"
          );
        }
        break;

      default:
    }
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onCellValueChanged: onCellValueChanged,
          onBtnAddClick: onGrid2BtnsClick,
          onBtnDeleteClick: onGrid2BtnsClick,
          onContextMenu: handleContextMenu,
          onBeforeAddRow: onBeforeAddRow2,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>

      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="49.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{
              isEditable: true,
              rowSelection: "single",
              title: "요청정보",
            }}
            events={grids.grid1.events}
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="49.5%">
          <SnGrid
            ref={gridRefs.current["grid2"]}
            columnConfig={grids.grid2.columns}
            rowData={grids.grid2.dataSource}
            etcProps={{
              isEditable: true,
              rowSelection: "multiple",
              title: "상세정보",
            }}
            events={grids.grid2.events}
          />
        </SnResizeBar>
        <SnFileUploadBox selectedDocu={selectedDocu} selectedLineDocu={0} />
      </div>
    </div>
  );
});

export default SN_PUR_PRQ_REG_001;
